import React, { Component } from "react";
import { connect } from "react-redux";
import BroadcastModal from "./BroadcastModal";
import FormBody from "./FormBody";
import CustomFormSlot from "./CustomFormSlot";
import BroadcastSession from "../../../services/BroadcastSession";
import {
  fetchDueBroadcasts, postBroadcastEvent, advanceBroadcastQueue, submitBroadcastResponse, downloadBroadcastPdf,
} from "../../../store/actions/broadcasts/broadcastActions";

/**
 * The employee pop-up renderer. Mounted once in the app layout (config/RouteList.js, SPEC 23.8),
 * for a logged-in user, so due items appear on whichever page the user opens; it is never
 * remounted by a route change, so /broadcasts/due runs once per login session.
 *
 * On mount it asks the engine for this login's queue (GET /broadcasts/due) and then shows
 * queue[queueIndex] — one modal at a time, in the order the server returned (mandatory first,
 * then priority; the max-per-login cap is the server's, nothing is dropped here).
 *
 *   shown        posted once per item when it becomes current
 *   dismissed    close X / Escape / backdrop / Close on a closable item -> on success, next item
 *   completed    Done -> on success, next item
 *   acknowledged / media_watched   posted as they happen
 *
 * A `form` item renders FormBody inside the modal; Submit asks the body to validate and POST
 * the answers (/responses). The server records completion there, so no `completed` event is
 * ever posted for a form (it would be a 422).
 *
 * A `custom` item (the migrated NHO / ITAM / COC / Happiness / Year-End Party pop-ups) renders
 * CustomFormSlot with the store's user and settings. The slot saves through its own endpoint
 * and reports back: onComplete -> `completed` (best-effort) then next item; onDefer (COC step 1)
 * -> next item, nothing posted; onSkip (a key this build does not know) -> `dismissed`
 * best-effort and next item regardless, so the user is never trapped behind a mandatory item.
 *
 * A failed event keeps the current item on screen — except on the custom path: the slot has
 * already saved through its own endpoint, so a rejected `completed` is logged and the queue
 * advances anyway (keeping the item would only make the user re-submit; the engine re-reads
 * completion from the slot table on the next due, SPEC 21.2). A failed due fetch renders nothing.
 *
 * Per-person forms (SPEC 23.5 / 23.6): the due item carries `assignments` (all pending, oldest
 * first). The one on screen is assignments[assignmentIndex]; its period_key ("A<id>") is used for
 * the events and the submit. After a successful Sign the item stays on screen as "Signed" with
 * Download copy and Next (the next pending assignment, while the server's `remaining` > 0) or
 * Close (the queue moves on; the server already recorded completion, so nothing is posted).
 */
class BroadcastQueue extends Component {
  // Items that already had their `shown` posted in this mount. The per-login-session memory (which
  // survives a Dashboard remount) lives in BroadcastSession (sessionStorage, keyed by the tab key).
  shownIds = {};

  // The current FormBody, so Submit can ask it to validate and send.
  formRef = React.createRef();

  state = {
    assignmentIndex: 0,   // per-person: which pending assignment of the current item is on screen
    signed: null,         // per-person: the server's content once the current one is signed
  };

  componentDidMount() {
    if (this.props.fetchDueBroadcasts) {
      this.props.fetchDueBroadcasts();
    }
    this.postShownIfNew();
  }

  componentDidUpdate(prevProps) {
    if ((prevProps.queueIndex || 0) !== (this.props.queueIndex || 0) && (this.state.assignmentIndex !== 0 || this.state.signed)) {
      this.setState({ assignmentIndex: 0, signed: null });
    }
    this.postShownIfNew();
  }

  queueItem = () => {
    const queue = Array.isArray(this.props.queue) ? this.props.queue : [];
    return queue[this.props.queueIndex || 0] || null;
  };

  assignments = (item) => (item && item.per_person && Array.isArray(item.assignments) ? item.assignments : []);

  // The item as it is shown: a per-person item carries the assignment on screen and its period key.
  current = () => {
    const item = this.queueItem();
    if (!item || !item.per_person) return item;
    const list = this.assignments(item);
    const assignment = list[this.state.assignmentIndex] || list[0] || null;
    if (!assignment) return item;
    return { ...item, assignment: assignment, period_key: assignment.period_key || "A" + assignment.id };
  };

  // Body for every event: the engine checks period_key against the one it computed.
  eventExtra = (item) => ({ period_key: item.period_key });

  postShownIfNew = () => {
    const item = this.current();
    if (!item || this.shownIds[item.id] || BroadcastSession.wasShown(item.id)) return;
    this.shownIds[item.id] = true;
    BroadcastSession.markShown(item.id);
    this.props.postBroadcastEvent(item.id, "shown", this.eventExtra(item));
  };

  // Resolves true when the engine accepted the event and the queue moved on.
  advanceAfter = (item, event) => {
    return this.props.postBroadcastEvent(item.id, event, this.eventExtra(item)).then((ok) => {
      if (ok) this.props.advanceBroadcastQueue();
      return ok;
    });
  };

  onDismiss = () => {
    const item = this.current();
    if (item) this.advanceAfter(item, "dismissed");
  };

  onComplete = () => {
    const item = this.current();
    if (!item) return Promise.resolve(false);
    if (item.kind === "form") return this.submitForm();
    if (item.kind === "custom") return this.advanceAfterCustomSave(item);
    return this.advanceAfter(item, "completed");
  };

  // The slot already saved: post `completed` best-effort, log a refusal, and always move on.
  advanceAfterCustomSave = (item) => {
    return this.props.postBroadcastEvent(item.id, "completed", this.eventExtra(item)).then((ok) => {
      if (!ok) {
        console.warn("[broadcasts] completed was not accepted for item " + item.id + " (" + String(item.custom_key) + "); advancing anyway — the slot already saved");
      }
      this.props.advanceBroadcastQueue();
      return true;
    });
  };

  // Resolves true when the body's answers were accepted (or already recorded) and the queue moved on.
  submitForm = () => {
    const body = this.formRef.current;
    if (!body) return Promise.resolve(false);
    const item = this.current();
    return body.submit().then((ok) => {
      if (!ok) return ok;
      if (item && item.per_person) {
        this.setState({ signed: body.result || {} });
      } else {
        this.props.advanceBroadcastQueue();
      }
      return ok;
    });
  };

  hasNext = () => {
    const signed = this.state.signed;
    if (!signed || !(Number(signed.remaining) > 0)) return false;
    return this.state.assignmentIndex + 1 < this.assignments(this.queueItem()).length;
  };

  onNext = () => this.setState({ assignmentIndex: this.state.assignmentIndex + 1, signed: null });

  onSignedClose = () => this.props.advanceBroadcastQueue();

  onDownload = (url) => {
    const item = this.current();
    const aid = item && item.assignment ? item.assignment.id : "";
    if (this.props.downloadBroadcastPdf) this.props.downloadBroadcastPdf(url, "document-" + aid + ".pdf");
  };

  signerName = () => {
    const user = this.props.user || {};
    return [user.first_name, user.last_name].filter(Boolean).join(" ");
  };

  submitResponse = (item) => (payload) => this.props.submitBroadcastResponse(item.id, payload);

  // The slot closed the item for now without completing it (COC step 1: the engine stops
  // showing it through isEligible until step 2 is due). Nothing is posted: a `dismissed` on a
  // mandatory item would be a 422.
  onDefer = () => {
    this.props.advanceBroadcastQueue();
  };

  // No slot registered for the item's key: post `dismissed` best-effort and move on without
  // waiting (the server may answer 422 for a mandatory item; the user still must not be stuck).
  onSkip = () => {
    const item = this.current();
    if (!item) return;
    this.props.postBroadcastEvent(item.id, "dismissed", this.eventExtra(item));
    this.props.advanceBroadcastQueue();
  };

  onAck = () => {
    const item = this.current();
    if (item) this.props.postBroadcastEvent(item.id, "acknowledged", this.eventExtra(item));
  };

  onWatched = () => {
    const item = this.current();
    if (item) this.props.postBroadcastEvent(item.id, "media_watched", this.eventExtra(item));
  };

  render() {
    const item = this.current();
    if (!item) return null;

    // Keyed by position (and the assignment on screen) so every item gets a fresh modal.
    const aid = item.assignment ? item.assignment.id : "";
    return (
      <BroadcastModal
        key={(this.props.queueIndex || 0) + ":" + item.id + ":" + aid}
        item={item}
        signed={this.state.signed}
        signedNext={this.hasNext()}
        onNext={this.onNext}
        onSignedClose={this.onSignedClose}
        onDownload={this.onDownload}
        onDismiss={this.onDismiss}
        onComplete={this.onComplete}
        onAck={this.onAck}
        onWatched={this.onWatched}
      >
        {item.kind === "form" ? <FormBody key={"f" + aid} ref={this.formRef} item={item} signerName={this.signerName()} submitResponse={this.submitResponse(item)} /> : null}
        {item.kind === "custom" ? (
          <CustomFormSlot
            item={item}
            user={this.props.user}
            settings={this.props.settings}
            onComplete={this.onComplete}
            onDefer={this.onDefer}
            onSkip={this.onSkip}
          />
        ) : null}
      </BroadcastModal>
    );
  }
}

const mapStateToProps = (state) => {
  const broadcasts = state.broadcasts || {};
  return {
    queue: broadcasts.queue,
    queueIndex: broadcasts.queueIndex,
    queueLoaded: broadcasts.queueLoaded,
    // For the custom slots (employee details, HR facilitator list).
    user: state.user,
    settings: state.settings,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchDueBroadcasts: () => dispatch(fetchDueBroadcasts()),
    postBroadcastEvent: (id, event, extra) => dispatch(postBroadcastEvent(id, event, extra)),
    advanceBroadcastQueue: () => dispatch(advanceBroadcastQueue()),
    submitBroadcastResponse: (id, payload) => dispatch(submitBroadcastResponse(id, payload)),
    downloadBroadcastPdf: (url, name) => dispatch(downloadBroadcastPdf(url, name)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(BroadcastQueue);
